/**
 * useReportPolling Hook
 * Implements intelligent polling strategy for report status updates
 *
 * Polling Intervals:
 * - PENDING/PROCESSING: 3-5 seconds (fast polling for initial generation)
 * - SUBMITTED: 5-10 seconds (slower polling for PDF generation)
 * - DRAFT/PUBLISHED/FAILED: Stop polling
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { reportService } from "../../../api/services/reportService";
import type { PollStatusResponse } from "../types";

interface UseReportPollingOptions {
  reportId: string | null;
  agencyId?: string | undefined;
  enabled?: boolean;
  /** Pre-seed the known status (e.g. from URL param) to skip the initial loading skeleton. */
  initialStatus?: PollStatusResponse["responseData"]["status"];
  onStatusChange?: (
    newStatus: PollStatusResponse["responseData"]["status"],
  ) => void;
  onDraft?: (reportId: string) => void;
  onPublished?: (reportId: string) => void;
  onFailed?: (reportId: string, errorMessage: string | null) => void;
}

export function useReportPolling({
  reportId,
  agencyId,
  enabled = true,
  initialStatus,
  onStatusChange,
  onDraft,
  onPublished,
  onFailed,
}: UseReportPollingOptions) {
  const previousStatusRef = useRef<string | null>(null);

  // Build placeholder data outside useQuery so TypeScript resolves the type exactly.
  const placeholderQueryData: PollStatusResponse | undefined = initialStatus
    ? {
        success: true,
        statusCode: 200,
        message: "",
        traceMessage: "",
        responseData: {
          report_id: reportId ?? "",
          status: initialStatus,
          draft_version: null,
          error_message: null,
          updated_at: "",
        },
      }
    : undefined;

  const { data, isLoading, error, refetch } = useQuery<
    PollStatusResponse,
    Error,
    PollStatusResponse,
    (string | null | undefined)[]
  >({
    queryKey: ["reportStatus", reportId, agencyId],
    queryFn: () => reportService.pollStatus(reportId!, agencyId),
    ...(placeholderQueryData !== undefined
      ? { placeholderData: placeholderQueryData }
      : {}),
    // Require agencyId so the X-T-Id header is always present
    enabled: !!reportId && !!agencyId && enabled,
    refetchInterval: (query) => {
      const status = query.state.data?.responseData?.status;

      // Stop polling for terminal states
      if (status === "DRAFT" || status === "PUBLISHED" || status === "FAILED") {
        return false;
      }

      // Fast polling for PENDING/PROCESSING (3-5 seconds, use 4s as middle ground)
      if (status === "PENDING" || status === "PROCESSING") {
        return 4000;
      }

      // Slower polling for SUBMITTED (5-10 seconds, use 7s as middle ground)
      if (status === "SUBMITTED") {
        return 7000;
      }

      // Fallback: stop polling for unknown states
      return false;
    },
    refetchOnWindowFocus: false,
  });

  // Track status changes and trigger callbacks
  useEffect(() => {
    if (data?.responseData) {
      const currentStatus = data.responseData.status;

      // Trigger onStatusChange if status changed
      if (previousStatusRef.current !== currentStatus) {
        previousStatusRef.current = currentStatus;
        onStatusChange?.(currentStatus);

        // Trigger specific callbacks for terminal states
        if (currentStatus === "DRAFT") {
          onDraft?.(reportId!);
        } else if (currentStatus === "PUBLISHED") {
          onPublished?.(reportId!);
        } else if (currentStatus === "FAILED") {
          onFailed?.(reportId!, data.responseData.error_message);
        }
      }
    }
  }, [data, reportId, onStatusChange, onDraft, onPublished, onFailed]);

  return {
    status: data?.responseData?.status,
    draftVersion: data?.responseData?.draft_version,
    errorMessage: data?.responseData?.error_message,
    updatedAt: data?.responseData?.updated_at,
    isPolling: isLoading,
    error,
    refetch,
  };
}
